import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level safety net: a render error anywhere below shows a friendly
 * "Something went wrong — reload" panel instead of a blank white page.
 * The error is still logged to the console for debugging.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen calc-grid-bg flex items-center justify-center px-4" role="alert">
        <div className="max-w-md rounded-2xl border border-[var(--js-border)] bg-white px-8 py-7 shadow-xl text-center">
          <AlertTriangle className="mx-auto h-9 w-9 text-[var(--js-red)]" aria-hidden="true" />
          <h1 className="mt-3 text-[16px] font-bold text-[var(--js-navy)]">
            Something went wrong — reload
          </h1>
          <p className="mt-2 text-[12px] font-medium leading-relaxed text-[var(--js-text-2)]">
            The page hit an unexpected error. Your inputs are saved in this
            browser, so reloading should bring them back.
          </p>
          <p className="mt-2 truncate text-[11px] text-[var(--js-text-3)]" title={this.state.error.message}>
            {this.state.error.message}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center gap-1.5 h-9 rounded-md bg-[var(--js-blue)] px-4 text-[12px] font-semibold text-white hover:bg-[#2d5577] transition-colors"
          >
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" /> Reload page
          </button>
        </div>
      </div>
    );
  }
}
